import type { AstralRarity, HeroClass, ItemHeroClass, SkillName } from "../types";
import astralArchetypes from "../data/astral_archetypes.json";

export type HeroGender = "m" | "w";

/** Baked at build time (Docker: CI commit SHA). Busts WebView cache for public/ assets. */
const STATIC_ASSET_VERSION = (import.meta.env.VITE_STATIC_ASSET_VERSION ?? "dev").trim() || "dev";

/** Append ?v=… to paths under /assets/ (files from public/, not Vite-hashed). */
export function staticAssetUrl(path: string): string {
  if (!path.startsWith("/assets/")) return path;
  if (/[?&]v=/.test(path)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}v=${encodeURIComponent(STATIC_ASSET_VERSION)}`;
}

const CLASS_FOLDER: Record<string, string> = {
  WARRIOR: "warrior",
  ARCHER: "archer",
  MAGE: "mage",
  RANGER: "archer",
  PRIEST: "mage",
};

export const CLASS_LABEL: Record<string, string> = {
  WARRIOR: "Warrior",
  ARCHER: "Archer",
  MAGE: "Mage",
  RANGER: "Archer",
  PRIEST: "Mage",
};

function itemClassFolder(heroClass: string | null | undefined): string {
  if (!heroClass) return "jewelry";
  return CLASS_FOLDER[heroClass] ?? "warrior";
}

export const CLASS_INFO: Record<
  HeroClass,
  { title: string; role: string; description: string }
> = {
  WARRIOR: {
    title: "Warrior",
    role: "Tank",
    description: "High HP and defense. Holds the front line and absorbs damage.",
  },
  ARCHER: {
    title: "Archer",
    role: "Damage dealer",
    description: "High attack and speed. Eliminates enemies from the back row.",
  },
  MAGE: {
    title: "Mage",
    role: "Healer & caster",
    description: "Heals allies, mass heals the party, and strikes with AoE magic.",
  },
};

/**
 * Returns path to the hero full-body image.
 * Each class/gender has its own folder so designers can swap assets independently.
 */
export function getConsumableIconPath(itemId: string): string {
  if (itemId.startsWith("mount_card_")) {
    return getMountIconPath(itemId.replace("mount_card_", ""));
  }
  return staticAssetUrl(`/assets/consumables/${itemId}.png`);
}

export function getMountIconPath(mountCode: string): string {
  return staticAssetUrl(`/assets/mounts/${mountCode}.png`);
}

export const MOUNT_RARITY_COLOR: Record<string, string> = {
  common: "border-gray-400 text-gray-300",
  uncommon: "border-green-500 text-green-300",
  rare: "border-blue-500 text-blue-300",
  epic: "border-purple-500 text-purple-300",
  legendary: "border-amber-500 text-amber-300",
};

export const CONSUMABLE_LABELS: Record<string, string> = {
  rename_scroll: "Rename Scroll",
  red_potion_t1: "Red Potion I",
  red_potion_t2: "Red Potion II",
  red_potion_t3: "Red Potion III",
  energy_potion: "Energy Potion",
  blood_pack: "Blood Pack",
  exp_scroll: "EXP Scroll",
  admin_exp_scroll: "Admin EXP Scroll",
  lucky_charm: "Lucky Charm",
  diamond_pack_s: "Diamond Pack S",
  dungeon_scroll: "Dungeon Scroll",
  vip_weekly_chest: "VIP Weekly Chest",
  roulette_ticket: "Roulette Ticket",
  mount_training_stone: "Mount Training Stone",
};

/** Max consumables usable in one POST /stash/use (must match backend). */
export const CONSUMABLE_MAX_USE_AT_ONCE = 1000;

/** Stash items that can be consumed via POST /stash/use. */
export const USABLE_STASH_ITEMS = new Set([
  "red_potion_t1",
  "red_potion_t2",
  "red_potion_t3",
  "energy_potion",
  "blood_pack",
  "exp_scroll",
  "admin_exp_scroll",
  "lucky_charm",
  "dungeon_scroll",
  "vip_weekly_chest",
]);

export function isMountCardItemId(itemId: string): boolean {
  return itemId.startsWith("mount_card_");
}

export function isUsableStashItem(itemId: string): boolean {
  return USABLE_STASH_ITEMS.has(itemId) || isMountCardItemId(itemId);
}

export function energyCostLabel(hero: { energy_costs?: Record<string, number> } | null, activity: string): string {
  const cost = hero?.energy_costs?.[activity];
  if (!cost) return "";
  return `⚡ ${cost}`;
}

export function hasEnergyFor(
  hero: {
    energy?: number;
    energy_costs?: Record<string, number>;
    newcomer_energy_bonus_hours_remaining?: number | null;
  } | null,
  activity: string,
): boolean {
  if (!hero) return false;
  const cost = hero.energy_costs?.[activity] ?? 0;
  if (cost <= 0) return true;
  const bonus = hero.newcomer_energy_bonus_hours_remaining;
  if (bonus != null && bonus > 0) return true;
  return (hero.energy ?? 0) >= cost;
}

export const CONSUMABLE_DESCRIPTIONS: Record<string, string> = {
  rename_scroll: "Change your hero name once. Use from the Hero screen.",
  red_potion_t1: "+5% ATK for 30 minutes",
  red_potion_t2: "+10% ATK for 30 minutes",
  red_potion_t3: "+20% ATK for 30 minutes",
  energy_potion: "Restores 50 energy",
  blood_pack: "Fully restores hero HP",
  exp_scroll: "Grants 500 bonus XP to your hero",
  admin_exp_scroll: "Grants 50,000 bonus XP to your hero (admin grant only)",
  lucky_charm: "A charm that brings a sliver of fortune",
  diamond_pack_s: "A small pack of rare materials",
  dungeon_scroll: "Grants +1 extra MP dungeon visit today. Use before entering.",
  vip_weekly_chest: "Open for gold, star dust, and diamonds (weekly VIP reward).",
};

export function consumableLabel(itemId: string): string {
  if (isMountCardItemId(itemId)) {
    const code = itemId.replace("mount_card_", "");
    return `${code.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} Card`;
  }
  return CONSUMABLE_LABELS[itemId] ?? itemId.replace(/_/g, " ");
}

export function getHeroImagePath(heroClass: HeroClass, gender: HeroGender = "m"): string {
  return staticAssetUrl(`/assets/heroes/${CLASS_FOLDER[heroClass]}-${gender}/hero.png`);
}

/** CSS object-position for circular avatar head crop (tune per class in styles.css). */
export function getHeroAvatarObjectPosition(heroClass: HeroClass): string {
  const key = CLASS_FOLDER[heroClass] ?? "warrior";
  return `var(--hero-avatar-pos-${key}, 50% 12%)`;
}

export const CLASS_CREST: Record<HeroClass, string> = {
  WARRIOR: "🛡️",
  ARCHER: "🏹",
  MAGE: "🔮",
};

export function getSkillIconPath(heroClass: HeroClass, skill: SkillName | string): string {
  const folder = itemClassFolder(heroClass);
  const file = skill.toLowerCase();
  return staticAssetUrl(`/assets/skills/${folder}/${file}.png`);
}

/** Passive icons live beside active skills, keyed by passive_code (e.g. acumen). */
export function getPassiveIconPath(heroClass: HeroClass, passiveCode: string): string {
  return getSkillIconPath(heroClass, passiveCode);
}

export function equipTierForLevel(itemLevel: number): string {
  if (itemLevel >= 80) return "set_80";
  if (itemLevel >= 70) return "set_70";
  if (itemLevel >= 60) return "set_60";
  if (itemLevel >= 50) return "set_50";
  if (itemLevel >= 40) return "set_40";
  if (itemLevel >= 11) return "junk_2";
  return "junk_1";
}

export function getItemIconPath(
  heroClass: ItemHeroClass | null | undefined,
  slot: string,
  icon: string,
  itemLevel?: number,
): string {
  const folder = itemClassFolder(heroClass);
  const slotFolder = slot.toLowerCase();
  const useTier = itemLevel != null && !icon.startsWith("catacomb_");
  const file = useTier ? equipTierForLevel(itemLevel) : icon;
  return staticAssetUrl(`/assets/items/${folder}/${slotFolder}/${file}.png`);
}

/** e.g. /assets/gems/patk_l5.png — gem_type from API (PATK, LUCK, …). */
export function getGemIconPath(gemType: string, level: number): string {
  return staticAssetUrl(`/assets/gems/${gemType.toLowerCase()}_l${level}.png`);
}

/** Fill colors for CSS-mask tinting of white astral sprites (catalog rarities). */
export const ASTRAL_RARITY_FILL: Record<AstralRarity, string> = {
  gray: "#9ca3af",
  green: "#22c55e",
  blue: "#3b82f6",
  purple: "#a855f7",
  orange: "#f97316",
  red: "#ef4444",
};

const ASTRAL_CODE_TO_ARCHETYPE: Record<string, string> = astralArchetypes.code_to_archetype;

/** Legacy astral template codes → canonical template code (backend TEMPLATE_ALIASES). */
const ASTRAL_ICON_ALIASES: Record<string, string> = {
  brutality: "might",
  mysticality: "might",
  enhanced_force: "enhanced_might",
  enhanced_mysticality: "enhanced_might",
  refined_force: "refined_might",
  refined_mysticality: "refined_might",
  pristine_force: "pristine_might",
  pristine_mysticality: "pristine_might",
  holy_force: "holy_might",
  holy_mysticality: "holy_might",
  enshieldment: "tenacity",
  enhanced_enshieldment: "enhanced_tenacity",
  refined_enshieldment: "refined_tenacity",
  pristine_enshieldment: "pristine_tenacity",
  charm: "precision",
  refined_charm: "refined_precision",
  pristine_charm: "pristine_precision",
  holy_charm: "holy_precision",
};

export function resolveAstralTemplateCode(templateCode: string): string {
  return ASTRAL_ICON_ALIASES[templateCode] ?? templateCode;
}

export function getAstralArchetypeId(templateCode: string): string {
  const code = resolveAstralTemplateCode(templateCode);
  return ASTRAL_CODE_TO_ARCHETYPE[code] ?? ASTRAL_CODE_TO_ARCHETYPE.might;
}

export function getAstralWhiteIconPath(templateCode: string): string {
  const archetype = getAstralArchetypeId(templateCode);
  return staticAssetUrl(`/assets/astrals/white/${archetype}.png`);
}

/** @deprecated Use getAstralWhiteIconPath + ASTRAL_RARITY_FILL mask tint instead. */
export function getAstralIconPath(templateCode: string): string {
  return getAstralWhiteIconPath(templateCode);
}

/** e.g. /assets/materials/shard_l40.png */
export function getShardIconPath(level: number): string {
  return staticAssetUrl(`/assets/materials/shard_l${level}.png`);
}

/** e.g. /assets/materials/legendary_stone_l40.png */
export function getLegendaryStoneIconPath(level: number): string {
  return staticAssetUrl(`/assets/materials/legendary_stone_l${level}.png`);
}

export const GEM_ICON_FALLBACK = "💎";
export const SHARD_ICON_FALLBACK = "🧩";
export const LEGENDARY_STONE_ICON_FALLBACK = "💠";

export const RARITY_COLOR: Record<string, string> = {
  JUNK: "text-gray-400 border-gray-600",
  COMMON: "text-emerald-400 border-emerald-600",
  RARE: "text-blue-400 border-blue-500",
  EPIC: "text-purple-400 border-purple-500",
  LEGENDARY: "text-gold-300 border-gold-500",
};

export const SLOT_SIDE: Record<string, "left" | "right"> = {
  HELM: "left",
  HANDS: "left",
  BODY: "left",
  WEAPON: "left",
  NECK_R: "right",
  NECK_L: "right",
  RING_R: "right",
  RING_L: "right",
};

export const SKILL_LABEL: Record<string, string> = {
  BASIC: "Basic Attack",
  SKILL_1: "Skill I",
  SKILL_2: "Skill II",
  ULT: "Ultimate",
  SKILL_4: "Skill IV",
  SKILL_5: "Skill V",
  SKILL_6: "Skill VI",
  SKILL_7: "Skill VII",
  SKILL_8: "Skill VIII",
};

/** Prefer API display name, then event skill_display, then generic fallback. */
export function resolveSkillDisplay(
  skill: string,
  skillNames?: Record<string, string> | null,
  skillDisplay?: string | null,
): string {
  if (skillDisplay) return skillDisplay;
  if (skillNames?.[skill]) return skillNames[skill];
  return SKILL_LABEL[skill] ?? skill;
}
